'use client'

import Link from 'next/link'
import { Stethoscope, ArrowUpRight } from 'lucide-react'
import { analytics, trackEvent } from '@/lib/analytics'
import { DIAGNOSE_CTA } from '@/lib/blog-diagnose-cta'
import { useImpressionEvent } from '@/hooks/useImpressionEvent'

/**
 * Post-read diagnose card, shown at the engaged-reader peak on symptom/problem
 * posts (server-gated by isDiagnosisPost). Routes to the photo-based /diagnose
 * flow — the site's highest-converting capture surface (July 2026 audit). Client
 * component only because it fires the cta_clicked event; the eligibility decision
 * lives in the server component that renders it.
 */
export function PostReadDiagnoseCTA({ slug }: { slug?: string }) {
  const location = DIAGNOSE_CTA.location.postRead
  // Denominator. Post-read placement means reaching this card is itself the
  // signal, so without an impression its 1 click / 28d is unreadable: it could
  // be a weak card or a card almost nobody scrolls to, and those call for
  // opposite fixes.
  const impressionRef = useImpressionEvent<HTMLAnchorElement>(
    location,
    { surface: 'post_read_diagnose_cta', ...(slug ? { slug } : {}) },
    { emit: analytics.ctaSurfaceShown },
  )
  return (
    <Link
      ref={impressionRef}
      href={DIAGNOSE_CTA.href}
      prefetch={false}
      onClick={() =>
        trackEvent('cta_clicked', {
          cta_location: location,
          cta_text: DIAGNOSE_CTA.label,
        })
      }
      className="block group"
    >
      <div className="ds-glass rounded-[20px] p-5 md:p-6 flex items-center gap-4 transition-[transform,box-shadow] duration-300 hover:-translate-y-1">
        <div className="flex-shrink-0 grid place-items-center w-12 h-12 rounded-2xl border border-white/70 bg-green-50/85 text-green-700 transition-colors group-hover:bg-green-100/90">
          <Stethoscope className="w-6 h-6" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-bold text-green-700 uppercase tracking-wide mb-1">
            Still not sure what is wrong?
          </div>
          <div className="font-editorial text-base md:text-lg font-semibold text-gray-900 group-hover:text-green-700 transition-colors">
            {DIAGNOSE_CTA.tagline}
          </div>
        </div>
        <ArrowUpRight className="w-5 h-5 text-gray-400 group-hover:text-green-600 flex-shrink-0 transition-colors" />
      </div>
    </Link>
  )
}
